import { Users } from 'lucide-react';
import { DynamicIcon } from 'lucide-react/dynamic';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, ProgressBar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ContributionInfo } from '../../service/api';
import api from '../../service/api';
import useShopItemsStore from '../../store/shopItems';
import CollectableShopItemModal from '../CollectableShopItemModal';
import EffectPrice from '../EffectPrice';
import ShopItemModal from '../ShopItemModal';

interface ShopItemCardProps {
  itemId: string;
}

const ShopItemCard: React.FC<ShopItemCardProps> = ({ itemId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contributionInfo, setContributionInfo] =
    useState<ContributionInfo | null>(null);
  const { t } = useTranslation();
  const getShopItemById = useShopItemsStore(state => state.getShopItemById);

  const item = useMemo(
    () => getShopItemById(itemId),
    [getShopItemById, itemId],
  );

  const isCollectable = item?.purchaseType === 'collectable';

  const loadContributions = useCallback(async () => {
    if (!isCollectable) return;
    try {
      const info = await api.shopGetContributions(itemId);
      setContributionInfo(info);
    } catch (error) {
      console.error('Failed to load contributions:', error);
    }
  }, [itemId, isCollectable]);

  useEffect(() => {
    loadContributions();
  }, [loadContributions]);

  const progressPercent = useMemo(() => {
    if (!item || !contributionInfo || item.price <= 0) return 0;
    return Math.min(
      Math.round((contributionInfo.totalCollected / item.price) * 100),
      100,
    );
  }, [item, contributionInfo]);

  if (!item) {
    return null;
  }

  const { title, price, icon } = item;

  return (
    <>
      <Card
        text="white"
        className="mb-3"
        style={{
          backgroundColor: 'rgba(6, 56, 96, 0.85)',
          cursor: 'pointer',
        }}
        onClick={() => setIsModalOpen(true)}
      >
        <Card.Body className="d-flex align-items-center gap-3">
          <div
            className="d-flex align-items-center justify-content-center rounded-3 bg-primary"
            style={{ width: '3rem', height: '3rem', flexShrink: 0 }}
          >
            <DynamicIcon name={icon} size={40} strokeWidth={1.5} />
          </div>

          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2">
              <Card.Title as="h3" className="h5 fw-bold mb-0">
                {title}
              </Card.Title>
              {isCollectable && (
                <span className="badge bg-info" style={{ fontSize: '0.65rem' }}>
                  <Users size={10} className="me-1" />
                  {t('shop.collectable')}
                </span>
              )}
            </div>

            {/* Progress bar for collectable items */}
            {isCollectable && contributionInfo && (
              <div className="mt-2">
                <ProgressBar
                  now={progressPercent}
                  variant={contributionInfo.isFullyFunded ? 'success' : 'info'}
                  style={{ height: '0.5rem' }}
                />
                <small className="text-white-50" style={{ fontSize: '0.7rem' }}>
                  {t('shop.progress', {
                    collected: contributionInfo.totalCollected,
                    total: price,
                  })}
                </small>
              </div>
            )}
          </div>

          <EffectPrice
            originalPrice={price}
            effectivePrice={item.effectivePrice}
          />
        </Card.Body>
      </Card>

      {/* Route to appropriate modal based on purchase type */}
      {isCollectable ? (
        <CollectableShopItemModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            loadContributions();
          }}
          itemId={itemId}
        />
      ) : (
        <ShopItemModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          itemId={itemId}
        />
      )}
    </>
  );
};

export default ShopItemCard;
export type { ShopItemCardProps };
